/**
 * "A step up" on the order screen: the better version of a product already on the order.
 *
 * Same manners as the cross-sell strip -- one attempt, nothing rendered when there is
 * nothing to say, the server's own sentence on every row -- because it sits in the same
 * corner of the same screen and must not slow the rep down either.
 */

import { useQuery } from '@tanstack/react-query'

import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { formatMoney } from '@/features/orders/api'
import { getUpsell, type Recommendation } from '@/features/recommendations/api'

export interface UpsellPanelProps {
  productIds: readonly string[]
  onAdd: (item: Recommendation) => void
  limit?: number
}

export function UpsellPanel({ productIds, onAdd, limit = 4 }: UpsellPanelProps) {
  const key = [...productIds].sort().join(',')
  const query = useQuery({
    queryKey: ['recommendations', 'upsell', key, limit],
    queryFn: () => getUpsell(productIds, limit),
    enabled: productIds.length > 0,
    retry: false,
    staleTime: 60_000,
  })

  const items = query.data
  if (query.isError || !items || items.length === 0) return null

  return (
    <Card>
      <CardHeader
        title="A step up"
        description="The better version of something already on the order."
      />
      <CardBody>
        <ul className="grid gap-2 tablet:grid-cols-2 desktop:grid-cols-3">
          {items.map((item) => (
            <li key={item.product_id}>
              <button
                type="button"
                onClick={() => onAdd(item)}
                className="flex w-full flex-col gap-1 rounded-md border border-slate-200 px-4 py-3 text-left hover:border-brand-500 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800"
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium">{item.name}</span>
                  <span className="text-sm tabular-nums">{formatMoney(item.price)}</span>
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{item.reason}</span>
              </button>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}
